import { Container, Sprite, Texture } from 'pixi.js'
import {
  DEATH_SCATTER_CLIENT_FX_ENABLED,
  DEATH_SCATTER_CLIENT_MAX_ACTIVE_SHARDS,
  DeathScatterBandV4,
  SPARK_SIZE,
} from '@star-snake/shared'
import type { DecodedDeathScatterBundleV4 } from '../netcode-v4/protocol/BinaryDecoderV4'
import { Camera } from '../game/Camera'
import { ParticleManager } from './ParticleManager'
import { NeonColorsHex } from '../ui/neon-theme'

interface ActiveShardFx {
  launchTick: number
  landingTick: number
  startX: number
  startZ: number
  targetX: number
  targetZ: number
  controlX: number
  controlZ: number
  value: number
  visualValue: number
  band: number
  useOriginGlow: boolean
  useLandingGlow: boolean
  head: Sprite
  originGlow: Sprite
  landingGlow: Sprite
  trail: Sprite[]
}

interface ShardTextureVariant {
  texture: Texture
  diameter: number
}

const VIEW_MARGIN_MULTIPLIER = 0.8
const TRAIL_OFFSETS = [0.14] as const
const BASE_SHARD_DIAMETER = SPARK_SIZE * 4
const SHARD_TEXTURE_PIXEL_SIZE = 3
const SHARD_CORE_RADIUS_CELLS = 2
const SHARD_GLOW_RADIUS_CELLS = 4
const SHARD_PADDING_CELLS = 4
const SHARD_CLUSTER_LAYOUTS = [
  [{ x: 0, y: 0 }],
  [{ x: -2.2, y: 0 }, { x: 2.2, y: 0 }],
  [{ x: 0, y: -2.3 }, { x: -2.0, y: 1.8 }, { x: 2.0, y: 1.8 }],
] as const
const BAND_TINTS = {
  [DeathScatterBandV4.NEAR]: NeonColorsHex.gold,
  [DeathScatterBandV4.MID]: NeonColorsHex.gold,
  [DeathScatterBandV4.FAR]: NeonColorsHex.gold,
} as const

export class DeathScatterRenderer {
  private readonly container: Container
  private readonly shards: ActiveShardFx[] = []
  private readonly shardPool: Array<{
    head: Sprite
    originGlow: Sprite
    landingGlow: Sprite
    trail: Sprite[]
  }> = []
  private readonly headTextures: ShardTextureVariant[]
  private readonly trailTexture: Texture
  private readonly scratchPoint = { x: 0, z: 0 }
  private readonly scratchPointB = { x: 0, z: 0 }

  constructor(
    parent: Container,
    private readonly camera: Camera,
    private readonly particles: ParticleManager,
  ) {
    this.container = new Container()
    parent.addChild(this.container)
    this.headTextures = this.createHeadTextures()
    this.trailTexture = this.createTrailTexture()
  }

  addBundles(
    bundles: readonly DecodedDeathScatterBundleV4[],
    launchTick: number,
    currentRenderTick: number,
  ): void {
    if (!DEATH_SCATTER_CLIENT_FX_ENABLED) return

    const viewport = this.camera.getViewport()
    const margin = Math.max(viewport.width, viewport.height) * VIEW_MARGIN_MULTIPLIER

    for (const bundle of bundles) {
      if (currentRenderTick >= bundle.landingTick) continue
      if (!this.intersectsViewport(bundle, viewport, margin)) continue
      if (this.shards.length >= DEATH_SCATTER_CLIENT_MAX_ACTIVE_SHARDS) break
      this.shards.push(this.createShard(bundle, launchTick))
    }
  }

  update(renderTick: number): void {
    let i = 0
    while (i < this.shards.length) {
      const shard = this.shards[i]
      const totalTicks = Math.max(1, shard.landingTick - shard.launchTick)
      const progress = Math.max(0, Math.min(1, (renderTick - shard.launchTick) / totalTicks))

      if (progress >= 1) {
        const emitLandingFx = shard.band === DeathScatterBandV4.NEAR
          || (shard.band === DeathScatterBandV4.MID && shard.value >= 2)
        if (emitLandingFx) {
          this.particles.emit('death_scatter_land', shard.targetX, shard.targetZ)
        }
        this.releaseShard(shard)
        const lastIndex = this.shards.length - 1
        if (i !== lastIndex) {
          this.shards[i] = this.shards[lastIndex]
        }
        this.shards.pop()
        continue
      }

      this.updateShardVisual(shard, progress)
      i++
    }
  }

  clear(): void {
    for (const shard of this.shards) {
      this.releaseShard(shard)
    }
    this.shards.length = 0
  }

  dispose(): void {
    this.clear()
    for (const pooled of this.shardPool) {
      pooled.originGlow.destroy()
      pooled.landingGlow.destroy()
      pooled.head.destroy()
      for (const trail of pooled.trail) {
        trail.destroy()
      }
    }
    this.shardPool.length = 0
    for (const variant of this.headTextures) {
      variant.texture.destroy(true)
    }
    this.trailTexture.destroy(true)
    this.container.parent?.removeChild(this.container)
    this.container.destroy()
  }

  private createShard(bundle: DecodedDeathScatterBundleV4, launchTick: number): ActiveShardFx {
    const tint = BAND_TINTS[bundle.band as DeathScatterBandV4] ?? NeonColorsHex.gold
    const visualValue = Math.max(1, Math.min(3, bundle.value))
    const headTexture = this.headTextures[visualValue - 1] ?? this.headTextures[0]
    const useOriginGlow = bundle.band === DeathScatterBandV4.NEAR
    const useLandingGlow = bundle.band !== DeathScatterBandV4.FAR
    const visuals = this.acquireShardVisuals(headTexture, tint, useOriginGlow, useLandingGlow)

    const dx = bundle.targetX - bundle.startX
    const dz = bundle.targetZ - bundle.startZ
    const distance = Math.max(1, Math.hypot(dx, dz))
    const normalX = -dz / distance
    const normalZ = dx / distance
    const curveSign = (bundle.seed & 1) === 0 ? 1 : -1
    const curveAmount = Math.min(420, distance * (0.12 + ((bundle.seed >> 1) & 0x7) * 0.01))
    const controlX = (bundle.startX + bundle.targetX) * 0.5 + normalX * curveAmount * curveSign
    const controlZ = (bundle.startZ + bundle.targetZ) * 0.5 + normalZ * curveAmount * curveSign

    return {
      launchTick,
      landingTick: bundle.landingTick,
      startX: bundle.startX,
      startZ: bundle.startZ,
      targetX: bundle.targetX,
      targetZ: bundle.targetZ,
      controlX,
      controlZ,
      value: bundle.value,
      visualValue,
      band: bundle.band,
      useOriginGlow,
      useLandingGlow,
      originGlow: visuals.originGlow,
      landingGlow: visuals.landingGlow,
      head: visuals.head,
      trail: visuals.trail,
    }
  }

  private updateShardVisual(shard: ActiveShardFx, progress: number): void {
    const eased = this.easeOutCubic(progress)
    const headPos = this.sampleQuadratic(shard, eased, this.scratchPoint)
    const prevPos = this.sampleQuadratic(shard, Math.max(0, eased - 0.018), this.scratchPointB)
    const dirX = headPos.x - prevPos.x
    const dirZ = headPos.z - prevPos.z
    const heading = Math.atan2(dirZ, dirX)
    const arcLift = Math.sin(progress * Math.PI)
    const baseDiameter = this.headTextures[Math.max(0, Math.min(this.headTextures.length - 1, shard.visualValue - 1))]?.diameter ?? 36
    const extraScale = 1 + Math.min(0.22, Math.log2(Math.max(1, shard.value)) * 0.06)
    const baseSize = baseDiameter * extraScale
    const headSize = baseSize * (1 + arcLift * 0.08)

    if (shard.useOriginGlow) {
      const originPulse = Math.max(0, 1 - progress * 3.2)
      shard.originGlow.x = shard.startX
      shard.originGlow.y = shard.startZ
      shard.originGlow.width = baseSize * (1.6 + originPulse * 2.8)
      shard.originGlow.height = baseSize * (1.6 + originPulse * 2.8)
      shard.originGlow.alpha = originPulse * 0.42
    }

    if (shard.useLandingGlow) {
      const landingPulse = Math.max(0, (progress - 0.62) / 0.38)
      shard.landingGlow.x = shard.targetX
      shard.landingGlow.y = shard.targetZ
      shard.landingGlow.width = baseSize * (0.9 + landingPulse * 2.6)
      shard.landingGlow.height = baseSize * (0.9 + landingPulse * 2.6)
      shard.landingGlow.alpha = landingPulse * 0.28
    }

    shard.head.x = headPos.x
    shard.head.y = headPos.z
    shard.head.width = headSize
    shard.head.height = headSize
    shard.head.rotation = heading
    shard.head.alpha = 0.88 + arcLift * 0.12

    for (let i = 0; i < shard.trail.length; i++) {
      const trailProgress = Math.max(0, progress - TRAIL_OFFSETS[i])
      const easedTrail = this.easeOutCubic(trailProgress)
      const trailPos = this.sampleQuadratic(shard, easedTrail, this.scratchPoint)
      const scale = 0.94 - i * 0.12
      shard.trail[i].x = trailPos.x
      shard.trail[i].y = trailPos.z
      shard.trail[i].rotation = heading
      shard.trail[i].width = headSize * (1.9 - i * 0.12)
      shard.trail[i].height = Math.max(9, headSize * 0.32 * scale)
      shard.trail[i].alpha = Math.max(0.18, (0.62 - i * 0.08) * (1 - progress * 0.2))
    }
  }

  private sampleQuadratic(shard: ActiveShardFx, t: number, out: { x: number; z: number }): { x: number; z: number } {
    const inv = 1 - t
    out.x = inv * inv * shard.startX + 2 * inv * t * shard.controlX + t * t * shard.targetX
    out.z = inv * inv * shard.startZ + 2 * inv * t * shard.controlZ + t * t * shard.targetZ
    return out
  }

  private intersectsViewport(
    bundle: DecodedDeathScatterBundleV4,
    viewport: { x: number; z: number; width: number; height: number },
    margin: number,
  ): boolean {
    const minX = viewport.x - margin
    const minZ = viewport.z - margin
    const maxX = viewport.x + viewport.width + margin
    const maxZ = viewport.z + viewport.height + margin

    const startInside = bundle.startX >= minX && bundle.startX <= maxX && bundle.startZ >= minZ && bundle.startZ <= maxZ
    const targetInside = bundle.targetX >= minX && bundle.targetX <= maxX && bundle.targetZ >= minZ && bundle.targetZ <= maxZ
    return startInside || targetInside
  }

  private acquireShardVisuals(
    headTexture: ShardTextureVariant,
    tint: number,
    useOriginGlow: boolean,
    useLandingGlow: boolean,
  ): {
    head: Sprite
    originGlow: Sprite
    landingGlow: Sprite
    trail: Sprite[]
  } {
    const pooled = this.shardPool.pop() ?? this.createShardVisuals()

    pooled.originGlow.texture = this.headTextures[0]?.texture ?? Texture.WHITE
    pooled.originGlow.tint = tint
    pooled.originGlow.visible = useOriginGlow
    pooled.originGlow.alpha = 0
    if (useOriginGlow) {
      this.container.addChild(pooled.originGlow)
    }

    pooled.landingGlow.texture = this.headTextures[0]?.texture ?? Texture.WHITE
    pooled.landingGlow.tint = tint
    pooled.landingGlow.visible = useLandingGlow
    pooled.landingGlow.alpha = 0
    if (useLandingGlow) {
      this.container.addChild(pooled.landingGlow)
    }

    pooled.head.texture = headTexture.texture
    pooled.head.tint = tint
    pooled.head.visible = true
    pooled.head.alpha = 1
    this.container.addChild(pooled.head)

    for (let i = 0; i < pooled.trail.length; i++) {
      const sprite = pooled.trail[i]
      sprite.texture = this.trailTexture
      sprite.tint = tint
      sprite.visible = true
      sprite.alpha = Math.max(0, 0.42 - i * 0.05)
      this.container.addChild(sprite)
    }

    return pooled
  }

  private createShardVisuals(): {
    head: Sprite
    originGlow: Sprite
    landingGlow: Sprite
    trail: Sprite[]
  } {
    const originGlow = new Sprite(this.headTextures[0]?.texture ?? Texture.WHITE)
    originGlow.anchor.set(0.5)

    const landingGlow = new Sprite(this.headTextures[0]?.texture ?? Texture.WHITE)
    landingGlow.anchor.set(0.5)

    const head = new Sprite(this.headTextures[0]?.texture ?? Texture.WHITE)
    head.anchor.set(0.5)

    const trail = TRAIL_OFFSETS.map(() => {
      const sprite = new Sprite(this.trailTexture)
      sprite.anchor.set(0.92, 0.5)
      return sprite
    })

    return { head, originGlow, landingGlow, trail }
  }

  private releaseShard(shard: ActiveShardFx): void {
    this.container.removeChild(shard.head)
    shard.head.visible = false

    if (shard.originGlow.parent === this.container) {
      this.container.removeChild(shard.originGlow)
    }
    shard.originGlow.visible = false

    if (shard.landingGlow.parent === this.container) {
      this.container.removeChild(shard.landingGlow)
    }
    shard.landingGlow.visible = false

    for (const trail of shard.trail) {
      if (trail.parent === this.container) {
        this.container.removeChild(trail)
      }
      trail.visible = false
    }

    this.shardPool.push({
      head: shard.head,
      originGlow: shard.originGlow,
      landingGlow: shard.landingGlow,
      trail: shard.trail,
    })
  }

  private easeOutCubic(t: number): number {
    if (t <= 0) return 0
    if (t >= 1) return 1
    return 1 - Math.pow(1 - t, 3)
  }

  private createHeadTextures(): ShardTextureVariant[] {
    const baseGridSize = this.getClusterGridSize(SHARD_CLUSTER_LAYOUTS[0])
    return SHARD_CLUSTER_LAYOUTS.map((layout) => {
      const gridSize = this.getClusterGridSize(layout)
      return {
        texture: this.createHeadTexture(layout, gridSize),
        diameter: BASE_SHARD_DIAMETER * (gridSize / baseGridSize),
      }
    })
  }

  private getClusterGridSize(lobes: readonly { x: number; y: number }[]): number {
    const maxOffset = lobes.reduce((max, lobe) => Math.max(max, Math.abs(lobe.x), Math.abs(lobe.y)), 0)
    const gridRadius = Math.ceil(maxOffset + SHARD_GLOW_RADIUS_CELLS + SHARD_PADDING_CELLS)
    return gridRadius * 2 + 1
  }

  private createHeadTexture(lobes: readonly { x: number; y: number }[], gridSize: number): Texture {
    const size = gridSize * SHARD_TEXTURE_PIXEL_SIZE
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return Texture.WHITE

    ctx.imageSmoothingEnabled = false
    const centerCell = (gridSize - 1) / 2
    for (const lobe of lobes) {
      this.drawSparkLobe(ctx, centerCell + lobe.x, centerCell + lobe.y)
    }

    return Texture.from(canvas)
  }

  private drawSparkLobe(ctx: CanvasRenderingContext2D, centerX: number, centerY: number): void {
    const minX = Math.floor(centerX - SHARD_GLOW_RADIUS_CELLS)
    const maxX = Math.ceil(centerX + SHARD_GLOW_RADIUS_CELLS)
    const minY = Math.floor(centerY - SHARD_GLOW_RADIUS_CELLS)
    const maxY = Math.ceil(centerY + SHARD_GLOW_RADIUS_CELLS)

    for (let gy = minY; gy <= maxY; gy++) {
      for (let gx = minX; gx <= maxX; gx++) {
        const dx = gx - centerX
        const dy = gy - centerY
        const distance = Math.hypot(dx, dy)
        if (distance > SHARD_GLOW_RADIUS_CELLS + 0.15) continue

        let alpha = 0
        if (distance <= SHARD_CORE_RADIUS_CELLS) {
          alpha = 0.55 + (1 - distance / Math.max(0.001, SHARD_CORE_RADIUS_CELLS)) * 0.38
        } else {
          alpha = 0.1 + (1 - (distance - SHARD_CORE_RADIUS_CELLS) / Math.max(0.001, SHARD_GLOW_RADIUS_CELLS - SHARD_CORE_RADIUS_CELLS)) * 0.2
        }

        ctx.fillStyle = `rgba(255,255,255,${Math.max(0, Math.min(1, alpha))})`
        ctx.fillRect(
          gx * SHARD_TEXTURE_PIXEL_SIZE,
          gy * SHARD_TEXTURE_PIXEL_SIZE,
          SHARD_TEXTURE_PIXEL_SIZE,
          SHARD_TEXTURE_PIXEL_SIZE,
        )
      }
    }

    const coreX = Math.round(centerX) * SHARD_TEXTURE_PIXEL_SIZE
    const coreY = Math.round(centerY) * SHARD_TEXTURE_PIXEL_SIZE
    ctx.fillStyle = 'rgba(255,255,255,0.98)'
    ctx.fillRect(coreX, coreY, SHARD_TEXTURE_PIXEL_SIZE, SHARD_TEXTURE_PIXEL_SIZE)
  }

  private createTrailTexture(): Texture {
    const canvas = document.createElement('canvas')
    canvas.width = 96
    canvas.height = 28
    const ctx = canvas.getContext('2d')
    if (!ctx) return Texture.WHITE

    ctx.imageSmoothingEnabled = false
    for (let x = 0; x < canvas.width; x += 4) {
      const t = x / Math.max(1, canvas.width - 4)
      const alpha = Math.pow(1 - t, 1.35) * 0.82
      const width = Math.max(2, 10 - t * 8)
      ctx.fillStyle = `rgba(255,255,255,${alpha})`
      ctx.fillRect(x, (canvas.height - width) * 0.5, 4, width)
    }

    return Texture.from(canvas)
  }

  getActiveShardCount(): number {
    return this.shards.length
  }
}
